import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DEFAULT_LEAD_SOURCE, type LeadSourceOption } from '../../shared/constants/lead-source.constants';

export type LeadAttribution = {
  leadSource: LeadSourceOption;
  campaignName?: string;
  adSetName?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  capturedAt: string;
};

@Injectable({ providedIn: 'root' })
export class LeadAttributionService {
  private readonly route = inject(ActivatedRoute);

  captureFromRoute(): LeadAttribution {
    const params = this.route.snapshot.queryParamMap;
    const utmSource = params.get('utm_source')?.trim() || undefined;
    const utmMedium = params.get('utm_medium')?.trim() || undefined;
    const utmCampaign = params.get('utm_campaign')?.trim() || undefined;
    const utmTerm = params.get('utm_term')?.trim() || undefined;
    const utmContent = params.get('utm_content')?.trim() || undefined;
    const campaignName =
      params.get('campaign_name')?.trim() ||
      params.get('campaign')?.trim() ||
      utmCampaign;
    const adSetName =
      params.get('adset_name')?.trim() ||
      params.get('ad_set')?.trim() ||
      utmContent;
    const explicitSource = params.get('lead_source')?.trim();

    return {
      leadSource: this.resolveLeadSource(explicitSource, utmSource, utmMedium),
      campaignName,
      adSetName,
      utmSource,
      utmMedium,
      utmCampaign,
      utmTerm,
      utmContent,
      capturedAt: new Date().toISOString(),
    };
  }

  private resolveLeadSource(
    explicit?: string,
    utmSource?: string,
    utmMedium?: string,
  ): LeadSourceOption {
    const normalized = (explicit || utmSource || '').toLowerCase();
    const medium = (utmMedium || '').toLowerCase();

    if (/google|gclid|googleads|adwords/.test(normalized) || medium === 'cpc' && /google/.test(normalized)) {
      return 'Google Ads';
    }
    if (/facebook|fb|meta|instagram|ig/.test(normalized)) {
      return 'Meta Ads';
    }
    if (/youtube|yt/.test(normalized)) {
      return 'YouTube';
    }
    if (/linkedin|li/.test(normalized)) {
      return 'LinkedIn';
    }
    if (/instagram/.test(explicit || '')) {
      return 'Instagram';
    }
    if (medium === 'social' || /social|twitter|x\.com|tiktok/.test(normalized)) {
      return 'Social Media';
    }
    if (explicit && this.isKnownSource(explicit)) {
      return explicit as LeadSourceOption;
    }
    return DEFAULT_LEAD_SOURCE;
  }

  private isKnownSource(value: string): boolean {
    const sources: LeadSourceOption[] = [
      'Seminars',
      'Walk-In',
      'Instagram',
      'Referral',
      'Website',
      'Google Ads',
      'Meta Ads',
      'YouTube',
      'LinkedIn',
      'Social Media',
    ];
    return sources.includes(value as LeadSourceOption);
  }
}
